/**
 * WorldRenderer - PixiJS-based world visualization
 * Renders creatures with procedural sprites, food, and handles camera controls
 */

import { useEffect, useRef, useCallback } from 'react';
import { Application, Container, Graphics, Sprite } from 'pixi.js';
import { useSimulationStore } from '../stores/simulationStore';
import { getSpriteGenerator } from '../utils/SpriteGenerator';
import { FoodType, type CreatureData, type FoodData } from '../types/simulation';

// Type for canvas event handlers (for cleanup)
interface CanvasEventHandlers {
  mousedown: (e: MouseEvent) => void;
  mousemove: (e: MouseEvent) => void;
  mouseup: () => void;
  mouseleave: () => void;
  wheel: (e: WheelEvent) => void;
  click: (e: MouseEvent) => void;
}

/** Convert HSL to RGB */
function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

interface WorldRendererProps {
  worldWidth?: number;
  worldHeight?: number;
  onCreatureClick?: (id: string) => void;
  onBackgroundClick?: (x: number, y: number) => void;
}

// Creature visual container with sprite and overlay
interface CreatureVisual {
  container: Container;
  sprite: Sprite;
  overlay: Graphics;
  lastGenomeKey: string;
}

export function WorldRenderer({
  worldWidth = 2000,
  worldHeight = 2000,
  onCreatureClick,
  onBackgroundClick,
}: WorldRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const worldContainerRef = useRef<Container | null>(null);
  const creaturesContainerRef = useRef<Container | null>(null);
  const foodContainerRef = useRef<Container | null>(null);
  const creatureVisualsRef = useRef<Map<string, CreatureVisual>>(new Map());
  const foodGraphicsRef = useRef<Map<string, Graphics>>(new Map());
  const eventHandlersRef = useRef<CanvasEventHandlers | null>(null);

  // Get state from store
  const creatures = useSimulationStore((state) => state.creatures);
  const food = useSimulationStore((state) => state.food);
  const selectedCreatureId = useSimulationStore((state) => state.selectedCreatureId);
  const cameraX = useSimulationStore((state) => state.cameraX);
  const cameraY = useSimulationStore((state) => state.cameraY);
  const cameraZoom = useSimulationStore((state) => state.cameraZoom);
  const setCamera = useSimulationStore((state) => state.setCamera);
  // setCameraZoom available via store but currently using wheel handler directly
  const _setCameraZoom = useSimulationStore((state) => state.setCameraZoom);
  void _setCameraZoom; // Suppress unused warning - reserved for future zoom controls

  // Dragging state
  const isDragging = useRef(false);
  const lastDragPos = useRef({ x: 0, y: 0 });

  // Initialize PixiJS
  useEffect(() => {
    if (!containerRef.current) return;

    const initPixi = async () => {
      const app = new Application();

      await app.init({
        background: '#0a0a12',
        resizeTo: containerRef.current!,
        antialias: false, // Disable for crisp pixel art
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });

      containerRef.current!.appendChild(app.canvas);
      appRef.current = app;

      // Create world container for camera transforms
      const worldContainer = new Container();
      worldContainer.sortableChildren = true;
      app.stage.addChild(worldContainer);
      worldContainerRef.current = worldContainer;

      // Draw world background and grid
      const background = new Graphics();
      background.rect(0, 0, worldWidth, worldHeight);
      background.fill({ color: 0x0a0a12 });
      worldContainer.addChild(background);

      // Grid
      const grid = new Graphics();
      const gridSize = 100;
      grid.setStrokeStyle({ width: 1, color: 0x1a1a2e, alpha: 0.5 });

      for (let x = 0; x <= worldWidth; x += gridSize) {
        grid.moveTo(x, 0);
        grid.lineTo(x, worldHeight);
      }
      for (let y = 0; y <= worldHeight; y += gridSize) {
        grid.moveTo(0, y);
        grid.lineTo(worldWidth, y);
      }
      grid.stroke();
      worldContainer.addChild(grid);

      // World border
      const border = new Graphics();
      border.setStrokeStyle({ width: 3, color: 0x4ecca3, alpha: 0.5 });
      border.rect(0, 0, worldWidth, worldHeight);
      border.stroke();
      worldContainer.addChild(border);

      // Food container (below creatures)
      const foodContainer = new Container();
      foodContainer.zIndex = 1;
      worldContainer.addChild(foodContainer);
      foodContainerRef.current = foodContainer;

      // Creatures container (above food)
      const creaturesContainer = new Container();
      creaturesContainer.zIndex = 2;
      worldContainer.addChild(creaturesContainer);
      creaturesContainerRef.current = creaturesContainer;

      // Center camera initially
      const centerX = app.screen.width / 2 - worldWidth / 2;
      const centerY = app.screen.height / 2 - worldHeight / 2;
      setCamera(-centerX, -centerY);

      // Create named event handlers for proper cleanup
      const handlers: CanvasEventHandlers = {
        mousedown: (e: MouseEvent) => {
          isDragging.current = true;
          lastDragPos.current = { x: e.clientX, y: e.clientY };
        },
        mousemove: (e: MouseEvent) => {
          if (!isDragging.current) return;

          const dx = e.clientX - lastDragPos.current.x;
          const dy = e.clientY - lastDragPos.current.y;
          lastDragPos.current = { x: e.clientX, y: e.clientY };

          // Get current camera state directly from store to avoid stale closures
          const state = useSimulationStore.getState();
          state.setCamera(state.cameraX - dx / state.cameraZoom, state.cameraY - dy / state.cameraZoom);
        },
        mouseup: () => {
          isDragging.current = false;
        },
        mouseleave: () => {
          isDragging.current = false;
        },
        wheel: (e: WheelEvent) => {
          e.preventDefault();
          const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
          // Get current zoom from store to avoid stale closure
          const state = useSimulationStore.getState();
          state.setCameraZoom(state.cameraZoom * zoomFactor);
        },
        click: (e: MouseEvent) => {
          if (isDragging.current) return;

          const rect = app.canvas.getBoundingClientRect();
          const screenX = e.clientX - rect.left;
          const screenY = e.clientY - rect.top;

          // Convert to world coordinates
          const worldX = (screenX - app.screen.width / 2) / cameraZoom + cameraX + worldWidth / 2;
          const worldY = (screenY - app.screen.height / 2) / cameraZoom + cameraY + worldHeight / 2;

          // Check if clicked on a creature
          let clickedCreature: string | null = null;
          for (const creature of creatures) {
            const dx = creature.x - worldX;
            const dy = creature.y - worldY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < creature.size + 5) {
              clickedCreature = creature.id;
              break;
            }
          }

          if (clickedCreature) {
            onCreatureClick?.(clickedCreature);
          } else {
            onBackgroundClick?.(worldX, worldY);
          }
        },
      };

      // Store handlers reference for cleanup
      eventHandlersRef.current = handlers;

      // Attach event listeners
      app.canvas.addEventListener('mousedown', handlers.mousedown);
      app.canvas.addEventListener('mousemove', handlers.mousemove);
      app.canvas.addEventListener('mouseup', handlers.mouseup);
      app.canvas.addEventListener('mouseleave', handlers.mouseleave);
      app.canvas.addEventListener('wheel', handlers.wheel, { passive: false });
      app.canvas.addEventListener('click', handlers.click);
    };

    initPixi();

    return () => {
      // Remove event listeners before destroying
      if (appRef.current && eventHandlersRef.current) {
        const canvas = appRef.current.canvas;
        const handlers = eventHandlersRef.current;
        canvas.removeEventListener('mousedown', handlers.mousedown);
        canvas.removeEventListener('mousemove', handlers.mousemove);
        canvas.removeEventListener('mouseup', handlers.mouseup);
        canvas.removeEventListener('mouseleave', handlers.mouseleave);
        canvas.removeEventListener('wheel', handlers.wheel);
        canvas.removeEventListener('click', handlers.click);
        eventHandlersRef.current = null;
      }

      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
      }
      creatureVisualsRef.current.clear();
      foodGraphicsRef.current.clear();
      getSpriteGenerator().clearCache();
    };
  }, [worldWidth, worldHeight]);

  // Update camera transform
  useEffect(() => {
    const app = appRef.current;
    const worldContainer = worldContainerRef.current;
    if (!app || !worldContainer) return;

    const centerX = app.screen.width / 2;
    const centerY = app.screen.height / 2;

    worldContainer.x = centerX - cameraX * cameraZoom - (worldWidth / 2) * cameraZoom;
    worldContainer.y = centerY - cameraY * cameraZoom - (worldHeight / 2) * cameraZoom;
    worldContainer.scale.set(cameraZoom);
  }, [cameraX, cameraY, cameraZoom, worldWidth, worldHeight]);

  // Render creatures with procedural sprites
  const renderCreatures = useCallback(
    (creatureData: CreatureData[]) => {
      const container = creaturesContainerRef.current;
      if (!container) return;

      const spriteGen = getSpriteGenerator();
      const existingIds = new Set(creatureData.map((c) => c.id));

      // Remove creatures that no longer exist
      for (const [id, visual] of creatureVisualsRef.current) {
        if (!existingIds.has(id)) {
          container.removeChild(visual.container);
          visual.sprite.destroy();
          visual.overlay.destroy();
          visual.container.destroy();
          creatureVisualsRef.current.delete(id);
        }
      }

      // Update or create creatures
      for (const creature of creatureData) {
        let visual = creatureVisualsRef.current.get(creature.id);

        // Generate genome key for sprite caching
        const genomeKey = `${creature.id.slice(0, 8)}-${Math.floor(creature.size)}-${Math.floor(creature.color.h / 10) * 10}`;

        if (!visual) {
          // Create new creature visual
          const creatureContainer = new Container();
          creatureContainer.eventMode = 'static';
          creatureContainer.cursor = 'pointer';

          // Generate sprite texture
          const texture = spriteGen.generate({
            id: creature.id,
            size: creature.size,
            color: creature.color.h,
          });

          const sprite = new Sprite(texture);
          sprite.anchor.set(0.5);
          // Scale sprite to match creature size (sprite is ~12-24px, creature size is ~5-20)
          const spriteScale = (creature.size * 2.5) / texture.width;
          sprite.scale.set(spriteScale);

          const overlay = new Graphics();

          creatureContainer.addChild(sprite);
          creatureContainer.addChild(overlay);
          container.addChild(creatureContainer);

          visual = {
            container: creatureContainer,
            sprite,
            overlay,
            lastGenomeKey: genomeKey,
          };
          creatureVisualsRef.current.set(creature.id, visual);
        }

        // Update sprite if genome changed (shouldn't happen often)
        if (visual.lastGenomeKey !== genomeKey) {
          const texture = spriteGen.generate({
            id: creature.id,
            size: creature.size,
            color: creature.color.h,
          });
          visual.sprite.texture = texture;
          const spriteScale = (creature.size * 2.5) / texture.width;
          visual.sprite.scale.set(spriteScale);
          visual.lastGenomeKey = genomeKey;
        }

        // Update sprite rotation (creature angle)
        // Add PI/2 because sprites face "up" by default
        visual.sprite.rotation = creature.angle + Math.PI / 2;

        // Update overlay (selection ring + energy bar)
        const overlay = visual.overlay;
        overlay.clear();

        const isSelected = creature.id === selectedCreatureId;
        const energyRatio = creature.energy / creature.maxEnergy;

        // Selection ring
        if (isSelected) {
          overlay.setStrokeStyle({ width: 3, color: 0xffffff });
          overlay.circle(0, 0, creature.size + 8);
          overlay.stroke();

          // Pulsing glow effect
          overlay.setStrokeStyle({ width: 2, color: 0x4ecca3, alpha: 0.5 });
          overlay.circle(0, 0, creature.size + 12);
          overlay.stroke();
        }

        // Energy bar (below creature)
        const barWidth = creature.size * 2;
        const barHeight = 3;
        const barY = creature.size + 8;

        // Background
        overlay.rect(-barWidth / 2, barY, barWidth, barHeight);
        overlay.fill({ color: 0x222222 });

        // Energy fill
        const energyColor = energyRatio > 0.5 ? 0x4ecca3 : energyRatio > 0.25 ? 0xf9ed69 : 0xe94560;
        overlay.rect(-barWidth / 2, barY, barWidth * energyRatio, barHeight);
        overlay.fill({ color: energyColor });

        // Border
        overlay.setStrokeStyle({ width: 1, color: 0x444444 });
        overlay.rect(-barWidth / 2, barY, barWidth, barHeight);
        overlay.stroke();

        // Update position
        visual.container.x = creature.x;
        visual.container.y = creature.y;
      }
    },
    [selectedCreatureId]
  );

  // Get food colors based on type
  const getFoodColors = useCallback((foodItem: FoodData): { core: number; glow: number } => {
    switch (foodItem.type) {
      case FoodType.PLANT:
        return { core: 0x32cd32, glow: 0x228b22 }; // Green
      case FoodType.MEAT:
        // Use creature's inherited hue if available, otherwise default red
        if (foodItem.color !== undefined) {
          // Convert hue (0-360) to RGB
          const hue = foodItem.color;
          const rgb = hslToRgb(hue / 360, 0.7, 0.4);
          const rgbGlow = hslToRgb(hue / 360, 0.7, 0.25);
          return {
            core: (rgb.r << 16) | (rgb.g << 8) | rgb.b,
            glow: (rgbGlow.r << 16) | (rgbGlow.g << 8) | rgbGlow.b,
          };
        }
        return { core: 0xcd5c5c, glow: 0x8b0000 }; // Red
      case FoodType.MINERAL:
        return { core: 0x6495ed, glow: 0x4169e1 }; // Blue
      default:
        return { core: 0x32cd32, glow: 0x228b22 }; // Default green
    }
  }, []);

  // Render food
  const renderFood = useCallback((foodData: FoodData[]) => {
    const container = foodContainerRef.current;
    if (!container) return;

    const existingIds = new Set(foodData.map((f) => f.id));

    // Remove food that no longer exists
    for (const [id, graphics] of foodGraphicsRef.current) {
      if (!existingIds.has(id)) {
        container.removeChild(graphics);
        graphics.destroy();
        foodGraphicsRef.current.delete(id);
      }
    }

    // Update or create food
    for (const foodItem of foodData) {
      let graphics = foodGraphicsRef.current.get(foodItem.id);

      if (!graphics) {
        graphics = new Graphics();
        container.addChild(graphics);
        foodGraphicsRef.current.set(foodItem.id, graphics);
      }

      graphics.clear();

      // Get colors based on food type
      const colors = getFoodColors(foodItem);
      const brightness = 0.5 + (foodItem.energy / 50) * 0.5;

      // Draw based on food type
      if (foodItem.type === FoodType.MEAT) {
        // Meat: chunky, angular shape
        const r = foodItem.radius;
        graphics.moveTo(-r, -r * 0.6);
        graphics.lineTo(r * 0.8, -r * 0.8);
        graphics.lineTo(r, r * 0.5);
        graphics.lineTo(-r * 0.5, r);
        graphics.lineTo(-r, -r * 0.6);
        graphics.fill({ color: colors.core, alpha: brightness });

        // Glow underneath
        graphics.circle(0, 0, r * 1.3);
        graphics.fill({ color: colors.glow, alpha: brightness * 0.3 });
      } else if (foodItem.type === FoodType.MINERAL) {
        // Mineral: crystalline shape
        const r = foodItem.radius;
        // Main crystal
        graphics.moveTo(0, -r);
        graphics.lineTo(r * 0.7, -r * 0.3);
        graphics.lineTo(r * 0.5, r * 0.8);
        graphics.lineTo(-r * 0.5, r * 0.8);
        graphics.lineTo(-r * 0.7, -r * 0.3);
        graphics.lineTo(0, -r);
        graphics.fill({ color: colors.core, alpha: brightness });

        // Glow
        graphics.circle(0, 0, r * 1.2);
        graphics.fill({ color: colors.glow, alpha: brightness * 0.3 });

        // Sparkle highlight
        graphics.circle(-r * 0.2, -r * 0.4, r * 0.2);
        graphics.fill({ color: 0xffffff, alpha: brightness * 0.7 });
      } else {
        // Plant: organic blob shape
        // Outer glow
        graphics.circle(0, 0, foodItem.radius * 1.5);
        graphics.fill({ color: colors.glow, alpha: brightness * 0.3 });

        // Core
        graphics.circle(0, 0, foodItem.radius);
        graphics.fill({ color: colors.core, alpha: brightness });

        // Highlight
        graphics.circle(-foodItem.radius * 0.3, -foodItem.radius * 0.3, foodItem.radius * 0.4);
        graphics.fill({ color: 0xffffff, alpha: brightness * 0.5 });
      }

      graphics.x = foodItem.x;
      graphics.y = foodItem.y;
    }
  }, [getFoodColors]);

  // Update rendering when data changes
  useEffect(() => {
    renderCreatures(creatures);
  }, [creatures, renderCreatures]);

  useEffect(() => {
    renderFood(food);
  }, [food, renderFood]);

  return <div ref={containerRef} className="world-renderer" />;
}
